/**
 * CoachMark (DESIGN-client §6): a callout anchored to `presentation.anchors[anchor]` (viewport
 * DOMRect published by the scene), with Next and a global Skip. Falls back to bottom-center when
 * the anchor is unknown; flips above the target when there is no room below.
 */
import { type CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import { useStore } from '../../store/store';
import { Button } from '../ui';

/**
 * What the mark can point at. `DOMRect` satisfies it, and so does a rectangle measured live from
 * the scene — the tutorial points at one card in the fan, which no static anchor knows about.
 */
export interface AnchorRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CoachMarkProps {
  anchor: string;
  text: string;
  /**
   * Point here instead of at `anchor`. For a target that moves (a card in the fan, mid-spring)
   * the anchor map is the wrong shape: it is republished on layout changes, not per frame.
   */
  rect?: AnchorRect | null;
  onNext?: () => void;
  onSkip?: () => void;
  nextLabel?: string;
  skipLabel?: string;
}

export interface CoachPlacement {
  left: number;
  top: number;
  side: 'above' | 'below' | 'none';
}

export interface PlaceCoachOptions {
  /**
   * A y below which the callout must never be drawn — in practice the top of the card fan.
   *
   * A coach mark that covers the hand is teaching the player about cards it is hiding, and the
   * fan is the bottom third of the screen on every layout, so "flip above the anchor" is not
   * enough on its own: an anchor *inside* the fan still leaves the box level with its neighbours.
   * Defaults to the viewport height, which reproduces the unconstrained behaviour exactly.
   */
  keepClearBelowY?: number;
}

export function placeCoach(
  rect: AnchorRect | undefined,
  size: { w: number; h: number },
  vp: { w: number; h: number },
  gap = 12,
  opts: PlaceCoachOptions = {},
): CoachPlacement {
  const limit = Math.min(opts.keepClearBelowY ?? vp.h, vp.h);
  // Highest the box may sit, and the lowest its bottom edge may reach.
  const minTop = 8;
  const maxBottom = limit - gap;

  if (!rect) {
    // No anchor: sit just above the protected area rather than at a fixed offset from the bottom,
    // which is what used to drop an unanchored mark straight onto the fan.
    return {
      left: (vp.w - size.w) / 2,
      top: Math.max(minTop, maxBottom - size.h),
      side: 'none',
    };
  }

  const left = Math.min(Math.max(8, rect.x + rect.width / 2 - size.w / 2), vp.w - size.w - 8);
  const below = rect.y + rect.height + gap;
  if (below + size.h <= maxBottom) return { left, top: below, side: 'below' };

  // Above: anchored to the target, or to the protected edge when the target is inside it.
  const ceiling = Math.min(rect.y, limit);
  return { left, top: Math.max(minTop, ceiling - gap - size.h), side: 'above' };
}

/**
 * The anchor to point at, falling back along the key's own family.
 *
 * The scene publishes zone anchors (`hand.zone`, `trick.center`, `bid.stack`) but not per-object
 * ones, so a script beat aimed at `hand.card:BJ` resolves to nothing and the mark loses its
 * target entirely. Falling back to the family's zone keeps it pointing at the right part of the
 * table instead of drifting to a default position.
 */
export function resolveAnchor(anchors: Record<string, DOMRect>, key: string): AnchorRect | undefined {
  const exact = anchors[key];
  if (exact) return exact;
  const family = key.split(/[.:]/)[0];
  if (!family) return undefined;
  for (const candidate of [`${family}.zone`, `${family}.center`, family]) {
    const hit = anchors[candidate];
    if (hit) return hit;
  }
  return undefined;
}

/** The y the callout must stay above: the top of the fan, from the scene's own safe insets. */
export function clearLineFor(hudSafe: { bottom: number } | null, viewportHeight: number): number {
  return hudSafe ? Math.max(0, viewportHeight - hudSafe.bottom) : viewportHeight;
}

export function CoachMark({ anchor, text, rect: override, onNext, onSkip, nextLabel = 'Next', skipLabel = 'Skip' }: CoachMarkProps) {
  const anchors = useStore((s) => s.presentation.anchors);
  const hudSafe = useStore((s) => s.presentation.hudSafe);
  const rect = override ?? resolveAnchor(anchors, anchor);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; side: 'above' | 'below' | 'none' }>({ left: 0, top: 0, side: 'none' });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const size = { w: el.offsetWidth, h: el.offsetHeight };
    const compute = () => {
      const vp = { w: window.innerWidth, h: window.innerHeight };
      setPos(placeCoach(rect, size, vp, 12, { keepClearBelowY: clearLineFor(hudSafe, vp.h) }));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [rect, text, hudSafe]);

  const arrowStyle: CSSProperties | undefined =
    pos.side === 'below'
      ? { top: -10, left: rect ? Math.max(12, Math.min(rect.x + rect.width / 2 - pos.left - 8, 240)) : 24, transform: 'rotate(135deg)' }
      : pos.side === 'above'
        ? { bottom: -10, left: rect ? Math.max(12, Math.min(rect.x + rect.width / 2 - pos.left - 8, 240)) : 24, transform: 'rotate(-45deg)' }
        : undefined;

  return (
    <div ref={ref} className="coach" style={{ left: pos.left, top: pos.top }} role="dialog" aria-live="polite" data-testid="coach-mark" data-anchor={anchor}>
      {arrowStyle && <span className="coach__arrow" style={arrowStyle} />}
      <div>{text}</div>
      <div className="coach__actions">
        {onSkip && (
          <Button variant="ghost" onClick={onSkip} testId="coach-skip">
            {skipLabel}
          </Button>
        )}
        {onNext && (
          <Button variant="primary" onClick={onNext} testId="coach-next">
            {nextLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
